import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogContent,
  FormHelperText,
} from "@mui/material";
import { useMutation } from "@apollo/client";
//Icons
//Import from project
import "../../../Style/dialogstyle.scss";
import { AuthContext } from "../../../Context/AuthContext";
import { DialogTitleComp } from "../../../Utils/Component";
import { CREATE_MANDATE, UPDATE_MANDATE } from "../../../Schema/Mandate";
import { SelectDatePicker } from "../../Include/SelectDatePicker";
import { MultilineSelectParties } from "../../Include/MultipleSelect";
import { useGetMandateById } from "../../../Hooks/useGetMandateById";

export default function MandateForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const [createMandate] = useMutation(CREATE_MANDATE, {
    onCompleted: ({ createMandate }) => {
      setLoading(false);
      if (createMandate?.isSuccess === true) {
        setAlert(true, "success", createMandate?.message);
        setRefetch();
        handleClose();
        resetForm();
      } else {
        setAlert(true, "error", createMandate?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateMandate] = useMutation(UPDATE_MANDATE, {
    onCompleted: ({ updateMandate }) => {
      setLoading(false);
      if (updateMandate?.isSuccess === true) {
        setAlert(true, "success", updateMandate?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateMandate?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    remark: Yup.string().nullable(),
    endDate: Yup.string(),
    startDate: Yup.string(),
    totalMember: Yup.number().required(t("required")),
    mandateName: Yup.string().required(t("required")),
    parties: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required(t("required")),
        })
      )
      .min(1, t("required"))
      .required(t("required")),
  });

  const formik = useFormik({
    initialValues: {
      remark: "",
      parties: [],
      endDate: "",
      startDate: "",
      totalMember: 0,
      mandateName: "",
    },
    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      if (dialogTitle === "Create") {
        createMandate({
          variables: {
            input: {
              ...values,
              mandateName: `${values?.mandateName}`,
              parties: values?.parties?.map((p) => p.id),
            },
          },
        });
      } else {
        updateMandate({
          variables: {
            id: editData?._id,
            input: {
              ...values,
              mandateName: `${values?.mandateName}`,
              parties: values?.parties?.map((p) => p.id),
            },
          },
        });
      }
    },
  });

  const {
    errors,
    values,
    touched,
    resetForm,
    handleSubmit,
    setFieldValue,
    getFieldProps,
  } = formik;

  const { mandateData, refetch } = useGetMandateById({ id: editData?._id });

  useEffect(() => {
    refetch();
    if (mandateData) {
      const partiesData = mandateData?.parties?.map((e) => ({
        id: e?._id,
        title: e?.partyName,
      }));
      setFieldValue("parties", partiesData || []);
      setFieldValue("remark", mandateData?.remark || "");
      setFieldValue("endDate", mandateData?.endDate || "");
      setFieldValue("startDate", mandateData?.startDate || "");
      setFieldValue("mandateName", mandateData?.mandateName || "");
      setFieldValue("totalMember", mandateData?.totalMember || 0);
    }
  }, [mandateData, open]);

  return (
    <Dialog open={open} className="dialog-container" maxWidth="sm" fullWidth>
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_mandate")
            : t("modal_title_update_mandate")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}> 
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_mandate")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  type="number"
                  placeholder={t("thead_mandate")}
                  {...getFieldProps("mandateName")}
                  error={Boolean(touched.mandateName && errors.mandateName)}
                  helperText={touched.mandateName && errors.mandateName}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_total_member")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <Typography className={t("class-name-field-title")}>
                        {t("thead_people")}
                      </Typography>
                    ),
                  }}
                  placeholder={t("thead_total_member")}
                  {...getFieldProps("totalMember")}
                  error={Boolean(touched.totalMember && errors.totalMember)}
                  helperText={touched.totalMember && errors.totalMember}
                />
              </Grid>

              <Grid item xs={12}>
                <MultilineSelectParties
                  value={values?.parties}
                  buttonAdd={true}
                  onChange={(val) => setFieldValue("parties", val)}
                  error={Boolean(touched.parties && errors.parties)}
                  helperText={touched.parties && errors.parties}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_start_date")}
                </Typography>
                <SelectDatePicker
                  value={values?.startDate}
                  onChange={(val) => setFieldValue("startDate", val)}
                />
                {!!errors?.startDate && touched?.startDate && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors?.startDate}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_end_date")}
                </Typography>
                <SelectDatePicker
                  value={values?.endDate}
                  onChange={(val) => setFieldValue("endDate", val)}
                />
                {!!errors?.endDate && touched?.endDate && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors?.endDate}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_remark")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  rows={2}
                  multiline
                  placeholder={t("thead_remark")}
                  {...getFieldProps("remark")}
                  error={Boolean(touched.remark && errors.remark)}
                  helperText={touched.remark && errors.remark}
                />
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    className={t("class-btn-create")}
                    fullWidth
                    onClick={handleSubmit}
                  >
                    {dialogTitle === "Create" ? t("create") : t("update")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
